import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  ConstrainedTextField,
  FieldComponent,
  FieldLocator,
  isConstrainedTextField,
} from '@app/common/configurable-wizards';
import { ConstrainedTextInput } from '@app/common/configurable-wizards/fields/ConstrainedTextField';
import { useConstrainedTextField } from '@app/common/configurable-wizards/fields/ConstrainedTextField/hooks';

import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

enum AddressTextFields {
  StreetAddress = 'StreetAddress',
  StreetAddress2 = 'StreetAddress2',
  City = 'City',
}

export const ConstrainedAddressFieldComponent: FieldComponent<ConstrainedTextField> = ({
  parent,
  field,
}) => {
  const { t } = useUserSettingsTranslation();
  const { register } = useFormContext();
  const { name, error, registerOptions } = useConstrainedTextField({ parent, field });

  const options = useMemo(() => {
    if (field.id !== AddressTextFields.City && field.validationRegExp) {
      return {
        ...registerOptions,
        pattern: {
          // eslint-disable-next-line i18next/no-literal-string
          value: new RegExp(field.validationRegExp, 'i'),
          message: field.validationMessage || (field.label && t(
            'constrained-address-field.validation.pattern|Pattern validation message for constrained address input',
            'Your address must be a valid physical address',
          )) || '',
        },
      };
    }

    return registerOptions;
  }, [field, registerOptions]);

  const inputProps = register(name, options);

  return (
    <ConstrainedTextInput
      field={field}
      error={error}
      {...inputProps}
    />
  );
};

export const ConstrainedAddressFieldLocator: FieldLocator = ({ field }) => (
  isConstrainedTextField(field) && field.id in AddressTextFields
    ? ConstrainedAddressFieldComponent
    : undefined
);
